import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { SettingsMenuView } from "../Settings/SettingsMenuView";
import type { SettingsMenuRow, SettingsMenuSection } from "../Settings/settingsMenu";
import type { IconName } from "../Icon/Icon";
import type { UiTheme } from "../theme";

// The top-of-hub actions (redesign track HR §1). Shared: the row model
// (keys · icons · i18n copy) lives here once; each platform mount injects the
// handlers as a seam (mobile = react-navigation, web = state), so the tiles
// render identically on mobile and the web-rnw console. Renders through the
// shared <SettingsMenuView> (icon-forward rows) — no new tile primitive.
//
//   Nuovo workout      → start a blank workout now
//   Ripeti workout     → repeat a previously logged workout
//   Costruisci oggi    → a draft from the muscles under MEV (AP-9b)
//   Pianifica programma→ author a multi-week Program (native ProgramEditor)
//   Pianifica workout  → plan a single session (Plan / composer mode:'single')
//
// Handlers are OPTIONAL → the row renders only when its surface exists on this
// platform (capability-gating, [[feedback_feature_gating_by_default]]). Mobile
// wires all four; web-rnw wires only the ones already lifted (Pianifica workout
// today — the logger + program editor aren't web surfaces yet, tracked as gaps
// in next/rnw-strangler.md §8). No dead row that leads nowhere.

export type CreateHubActionsProps = {
  theme: UiTheme;
  onNewWorkout?: () => void;
  onRepeatWorkout?: () => void;
  onPlanProgram?: () => void;
  onPlanWorkout?: () => void;
  /** AP-9b: open the logger on a draft built from the volume gaps. */
  onBuildToday?: () => void;
  /** #492 F5: under the actions, only with «Build today's session» (the
   *  bodyweight toggle). */
  buildTodayExtra?: ReactNode;
  /** AP-9b D3: today's plan session, drawn above the actions. Present, it
   *  takes the visual lead and the build row says it replaces it. */
  todaySession?: ReactNode;
};

export function CreateHubActions({
  theme,
  onNewWorkout,
  onRepeatWorkout,
  onPlanProgram,
  onPlanWorkout,
  onBuildToday,
  buildTodayExtra,
  todaySession,
}: CreateHubActionsProps) {
  const { t } = useTranslation();

  const defs: Array<{
    key: string;
    icon: IconName;
    title: string;
    sub: string;
    onPress?: () => void;
  }> = [
    {
      key: "newWorkout",
      icon: "dumbbell",
      title: t("createHub.newWorkout.title", { defaultValue: "New workout" }),
      sub: t("createHub.newWorkout.sub", { defaultValue: "Start a blank workout now" }),
      onPress: onNewWorkout,
    },
    {
      key: "repeatWorkout",
      // 'refresh' to match the existing Repeat quick-start icon in PlanView.
      icon: "refresh",
      title: t("createHub.repeatWorkout.title", { defaultValue: "Repeat workout" }),
      sub: t("createHub.repeatWorkout.sub", { defaultValue: "Log again from a past workout" }),
      onPress: onRepeatWorkout,
    },
    {
      key: "buildToday",
      icon: "target",
      title: t("createHub.buildToday.title", {
        defaultValue: "Build today's session",
      }),
      sub: todaySession
        ? t("createHub.buildToday.subInstead", {
            defaultValue: "Instead of today's plan: the muscles below minimum volume",
          })
        : t("createHub.buildToday.sub", {
            defaultValue: "Exercises for the muscles below minimum volume",
          }),
      onPress: onBuildToday,
    },
    {
      key: "planProgram",
      icon: "calendar",
      title: t("createHub.planProgram.title", { defaultValue: "Plan a program" }),
      sub: t("createHub.planProgram.sub", { defaultValue: "Build your own program" }),
      onPress: onPlanProgram,
    },
    {
      key: "planWorkout",
      icon: "edit",
      title: t("createHub.planWorkout.title", { defaultValue: "Plan a workout" }),
      sub: t("createHub.planWorkout.sub", { defaultValue: "Prepare a single session for later" }),
      onPress: onPlanWorkout,
    },
  ];

  const live = defs.filter((d) => d.onPress);
  const rows: SettingsMenuRow[] = live.map(({ key, icon, title, sub }) => ({
    key,
    icon,
    title,
    sub,
  }));
  const sections: SettingsMenuSection[] = [
    ...(todaySession
      ? [
          {
            label: t("createHub.todayHeader", { defaultValue: "Today" }),
            rows: [],
            custom: todaySession,
          },
        ]
      : []),
    {
      label: t("createHub.actionsHeader", { defaultValue: "Start" }),
      rows,
      ...(onBuildToday && buildTodayExtra ? { custom: buildTodayExtra } : {}),
    },
  ];

  const onSelect = (key: string) => {
    live.find((d) => d.key === key)?.onPress?.();
  };

  return <SettingsMenuView theme={theme} sections={sections} onSelect={onSelect} />;
}
